import { useCallback, useEffect } from 'react';

export function useFormScroll() {
  const scrollIntoView = useCallback((element: HTMLElement | null) => {
    if (!element) return;
    
    // Small delay to let keyboard open
    setTimeout(() => {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 300);
  }, []);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    scrollIntoView(e.target);
  }, [scrollIntoView]);

  return { handleFocus, scrollIntoView };
}

// Custom hook for auto-scroll on mobile keyboard
export function useMobileFormScroll(containerRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.tagName === 'SELECT'
      ) {
        // Delay to allow virtual keyboard to appear
        setTimeout(() => {
          // Check if we're on mobile (virtual keyboard likely present)
          const viewportHeight = window.visualViewport?.height || window.innerHeight;
          const windowHeight = window.innerHeight;
          
          // If viewport is significantly smaller, keyboard is likely open
          const keyboardOpen = viewportHeight < windowHeight * 0.75;
          
          if (keyboardOpen || window.innerWidth < 768) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            
            // Additional padding from bottom for keyboard
            const rect = target.getBoundingClientRect();
            const visibleBottom = viewportHeight - 20;
            
            if (rect.bottom > visibleBottom) {
              const scrollBy = rect.bottom - visibleBottom + 50;
              container.scrollBy({
                top: scrollBy,
                behavior: 'smooth'
              });
            }
          }
        }, 350);
      }
    };

    // Handle visual viewport resize (keyboard open/close)
    const handleViewportResize = () => {
      const activeElement = document.activeElement as HTMLElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' || 
         activeElement.tagName === 'TEXTAREA' || 
         activeElement.tagName === 'SELECT')
      ) {
        setTimeout(() => {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    };

    container.addEventListener('focusin', handleFocusIn);
    
    // Listen for visual viewport changes (keyboard appearing)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
    };
  }, [containerRef]);
}
